import type { ReactNode } from "react";
import Markdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { FADE_TAIL_HREF, prepareChatMarkdown } from "@/lib/chat/chatMarkdown";
import ChatApprovalControl from "./ChatApprovalControl";
import ChatInstructionChangeControl from "./ChatInstructionChangeControl";
import ReadMore from "./ReadMore";
import remarkReadMore from "@/lib/chat/readMoreMarkdown";
import { toneBadge } from "@/lib/statusTone";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  source: "chat" | "cron" | "webhook" | "manual" | "poll";
  createdAt: number;
  streaming?: boolean;
}

const SOURCE_BADGES: Record<string, { label: string; className: string }> = {
  cron: {
    label: "Scheduled run",
    className: "bg-emerald-500/10 text-emerald-400",
  },
  webhook: { label: "Webhook", className: "bg-sky-500/10 text-sky-400" },
  manual: {
    label: "Manual run",
    className: "bg-violet-500/10 text-violet-300",
  },
  poll: { label: "Polled", className: "bg-amber-500/10 text-amber-400" },
};

function instructionChangeLink(href: string | undefined): { workerId: string; changeId: string } | null {
  const match = href?.match(/^\/workers\/([^/?#]+)\?instructionChange=([^&#]+)$/);
  if (!match) return null;
  try { return { workerId: decodeURIComponent(match[1]), changeId: decodeURIComponent(match[2]) }; }
  catch { return null; }
}

function approvalLink(href: string | undefined): { workerId: string; revisionId: string } | null {
  const match = href?.match(/^\/workers\/([^/?#]+)\?approveRevision=([^&#]+)$/);
  if (!match) return null;
  try { return { workerId: decodeURIComponent(match[1]), revisionId: decodeURIComponent(match[2]) }; }
  catch { return null; }
}

/**
 * Assistant replies render markdown; the user's own text does not.
 * Typing "2 * 3 * 4" in the composer should come back as typed, not as
 * italics — the user did not opt into markup by using the chat.
 */
const MARKDOWN_COMPONENTS: Components = {
  // Standalone bold lines are promoted to h3 upstream; every heading level
  // gets the same treatment because the models here never nest them.
  h1: Heading,
  h2: Heading,
  h3: Heading,
  h4: Heading,
  h5: Heading,
  h6: Heading,
  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
  strong: ({ children }) => (
    <strong className="font-semibold text-white">{children}</strong>
  ),
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => (
    <ul className="mb-2 list-disc space-y-0.5 pl-5 last:mb-0">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="mb-2 list-decimal space-y-0.5 pl-5 last:mb-0">{children}</ol>
  ),
  li: ({ children }) => <li className="pl-0.5">{children}</li>,
  a: ({ href, children }) => {
    if (href === FADE_TAIL_HREF) return (
      // The word that just arrived. Keyed on its text so React mounts a new
      // node each time, which is what restarts the fade — reusing the node
      // would leave the animation already finished.
      <span key={String(children)} className="caw-word-in">
        {children}
      </span>
    );
    const approval = approvalLink(href);
    if (approval) return <ChatApprovalControl {...approval} />;
    const change = instructionChangeLink(href);
    if (change) return <ChatInstructionChangeControl {...change} />;
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="text-violet-300 underline underline-offset-2 hover:text-violet-200"
      >
        {children}
      </a>
    );
  },
  code: ({ children }) => (
    <code className="rounded bg-violet-500/12 px-1 py-0.5 font-mono text-[12.5px] text-violet-200">
      {children}
    </code>
  ),
  pre: ({ children }) => (
    <pre className="mb-2 overflow-x-auto rounded-lg border border-zinc-800 bg-zinc-950/60 p-2.5 font-mono text-[12.5px] last:mb-0">
      {children}
    </pre>
  ),
  blockquote: ({ children, node }) =>
    // The technical part of a reply (ids, attempt counts, a tool's exact error), folded
    // away: the person reads the plain sentence above it. Its bold label is the summary.
    (node as { properties?: Record<string, unknown> } | undefined)?.properties?.["data-read-more"] ? (
      <ReadMore className="mb-2 last:mb-0">{children}</ReadMore>
    ) :
    // Smithers' one way of asking for something. It is drawn as a card so it
    // cannot hide in prose (the request of 2026-09-22 was buried in 5,000 characters).
    /^\s*(?:\*\*)?needs you/i.test(textOf(node)) ? (
      <div role="note" aria-label="Needs you" className="mb-2 flex gap-2.5 rounded-xl border border-attention/70 bg-attention/10 px-3.5 py-2.5 text-[15px] text-zinc-50 last:mb-0">
        <span aria-hidden="true" className="mt-0.5 shrink-0 text-attention">⚠</span>
        <div className="min-w-0 [&_code]:bg-zinc-800 [&_code]:text-attention [&_p]:mb-0 [&_strong]:text-attention">{children}</div>
      </div>
    ) : (
      <blockquote className="mb-2 border-l-2 border-zinc-700 pl-3 text-zinc-400 last:mb-0">
        {children}
      </blockquote>
    ),
  hr: () => <hr className="my-3 border-zinc-800" />,
  table: ({ children }) => (
    <div className="mb-2 overflow-x-auto last:mb-0">
      <table className="w-full border-collapse text-left">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border-b border-zinc-700 py-1 pr-3 font-medium">{children}</th>
  ),
  td: ({ children }) => (
    <td className="border-b border-zinc-800 py-1 pr-3">{children}</td>
  ),
};

/** The plain text of a markdown node, for telling one kind of blockquote from another. */
function textOf(node: unknown): string {
  const n = node as { type?: string; value?: string; children?: unknown[] } | undefined;
  if (!n) return "";
  if (n.type === "text") return n.value ?? "";
  return (n.children ?? []).map(textOf).join("");
}

function Heading({ children }: { children?: ReactNode }) {
  // Tailwind resets heading sizes, so colour alone left these at body size —
  // which reads as an arbitrarily tinted sentence rather than a heading.
  // Size and spacing carry the hierarchy; colour only reinforces it.
  return (
    <h3 className="mt-4 mb-1.5 text-[15px] leading-snug font-semibold tracking-tight text-violet-200 first:mt-0">
      {children}
    </h3>
  );
}

export default function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === "user";
  const badge = SOURCE_BADGES[message.source];
  const isError =
    message.role === "assistant" && message.content.startsWith("Run failed:");
  // Error notes are raw server strings, not authored prose — rendering them
  // as markdown would mangle stack traces and tool slugs.
  const isMarkdown = !isUser && !isError;

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[88%] sm:max-w-[78%] overflow-hidden rounded-2xl px-3.5 py-2 sm:px-4 sm:py-2.5 text-sm leading-relaxed break-words ${
          isUser
            ? message.source === "chat"
              ? "bg-violet-600 text-white"
              : "border border-zinc-700 bg-zinc-800/70 text-zinc-300"
            : isError
              ? `border ${toneBadge.danger}`
              : "border border-zinc-800 bg-zinc-900 text-zinc-100"
        }`}
      >
        {badge && (
          <span
            className={`mb-1.5 inline-block rounded-full px-2 py-0.5 text-[10px] font-medium tracking-wide uppercase ${badge.className}`}
          >
            {badge.label}
          </span>
        )}
        <div className={isMarkdown ? "" : "whitespace-pre-wrap"}>
          {isMarkdown ? (
            <Markdown
              remarkPlugins={[remarkGfm, remarkReadMore]}
              components={MARKDOWN_COMPONENTS}
            >
              {prepareChatMarkdown(message.content, {
                streaming: message.streaming,
              })}
            </Markdown>
          ) : (
            message.content
          )}
        </div>
        <div
          className={`mt-1 text-[10px] ${isUser && message.source === "chat" ? "text-violet-200/70" : "text-zinc-500"}`}
        >
          {new Date(message.createdAt).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </div>
      </div>
    </div>
  );
}
